import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiEndpoint } from "../../utils/ApiEndpoint";
import { httpClient } from "../../utils/HttpClient";
import { useAuth } from "../../Context/AuthContext";
import CardsInfo from "./CardsInfo";
import CardsData from "./CardsData";
import { WarningMessage } from "../../utils/toastify";

const _Monitor = () => {
  const auth = useAuth();

  const { token } = auth.getSessionData();
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState("");

  const {
    data: monitorData,
    dataUpdatedAt,
    refetch: refetchData,
    isFetching,
  } = useQuery({
    queryKey: ["monitor"],
    queryFn: async () => {
      const res = await httpClient(
        "GET",
        `${ApiEndpoint}monitor/services`,
        {},
        token,
      );
      const data = res.data;
      const sendNotify = data.some((item) => {
        if (item.vc_conexion === "DESCONECTADO" && !item.vc_statussmpp) {
          return item;
        }
      });

      if (sendNotify) {
        WarningMessage("Existen servicios inactivos y desconectados");
      }

      return data;
    },
    suspense: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: 30000,
  });

  const summaryData = useMemo(() => {
    const list = monitorData ?? [];
    let summary = {
      smy: list.length,
      status: {
        ok: 0,
        disconnected: 0,
        error: 0,
      },
    };

    for (const item of list) {
      switch (item.vc_conexion) {
        case "OK":
          summary.status.ok++;
          break;
        case "DESCONECTADO":
          summary.status.disconnected++;
          break;
        case "ERROR":
          summary.status.error++;
          break;
      }
    }

    return summary;
  }, [monitorData]);

  const filterData = useMemo(() => {
    const list = monitorData ?? [];

    return list.filter((item) => {
      const matchStatus = filter ? item.vc_conexion === filter : true;

      const matchSearch = search
        ? Object.values(item)
            .join(" ")
            .toLowerCase()
            .includes(search.toLowerCase())
        : true;

      return matchStatus && matchSearch;
    });
  }, [monitorData, filter, search]);

  return (
    <div className="w-full space-y-3">
      <div className="bg-white w-full flex items-center justify-between rounded-lg px-4 py-2 gap-2">
        <button
          className="border-none flex items-center rounded-lg px-4 py-2 gap-2"
          onClick={refetchData}
          disabled={isFetching}
        >
          <span className="material-symbols-outlined">refresh</span>
          <span className="text-sm">Actualizar</span>
        </button>

        {isFetching ? (
          <div className="flex items-center">
            <span className="text-sm">Actualizando...</span>
            <span className="material-symbols-outlined">sync</span>
          </div>
        ) : (
          <div className="flex items-center">
            <span className="text-sm mr-2">Actualizado:</span>
            <span className="material-symbols-outlined">schedule</span>
            <span className="font-semibold">
              {new Date(dataUpdatedAt).toLocaleString()}
            </span>
          </div>
        )}
      </div>
      <CardsInfo data={summaryData} setFilter={setFilter} filter={filter} />
      <div className="bg-white px-4 py-1 rounded-lg flex items-center">
        <div className="relative my-2 w-full">
          <span className="material-symbols-outlined absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400">
            search
          </span>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="w-full pl-12 py-1.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900 placeholder-slate-400 transition-all"
          />
        </div>
      </div>
      <CardsData data={filterData} />
    </div>
  );
};

export default _Monitor;
